import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { completeCreditsIntro as completeCreditsIntroForAccount } from "../../../Redux/actions/commonActtion";
import "./CreditsIntro.css";

export const CREDITS_INTRO_DELAY_MS = 15_000;

const storageKeyFor = (userId, projectId) =>
  `scrollx:credits-intro-completed:${userId}:${projectId}`;

const readCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch (_error) {
    return null;
  }
};

const updateStoredUser = (projectId) => {
  const user = readCurrentUser();
  if (!user) return;

  const completedProjectIds = Array.from(
    new Set([...(user.creditsIntroCompletedProjectIds || []), projectId]),
  );

  localStorage.setItem(
    "user",
    JSON.stringify({
      ...user,
      creditsIntroCompletedProjectIds: completedProjectIds,
    }),
  );
};

export default function CreditsIntro({
  active,
  entryKey,
  accountUser,
  projectId,
  projectEligible,
  creditSheetUrl,
  delayMs = CREDITS_INTRO_DELAY_MS,
}) {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [completedProjectId, setCompletedProjectId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const primaryActionRef = useRef(null);
  const user = accountUser || readCurrentUser();
  const userId = user?._id;
  const storageKey =
    userId && projectId ? storageKeyFor(userId, projectId) : null;
  const completedProjectIds = user?.creditsIntroCompletedProjectIds || [];
  const isEligible = Boolean(
    userId &&
      projectId &&
      projectEligible === true &&
      !completedProjectIds.includes(projectId) &&
      completedProjectId !== projectId &&
      localStorage.getItem(storageKey) !== "true",
  );

  useEffect(() => {
    if (!active || !isEligible) {
      setIsOpen(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setIsOpen(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [active, delayMs, entryKey, isEligible]);

  useEffect(() => {
    if (isOpen) primaryActionRef.current?.focus();
  }, [isOpen]);

  const completeIntro = async () => {
    if (
      !userId ||
      !projectId ||
      completedProjectId === projectId ||
      isSaving
    ) {
      return;
    }

    setIsSaving(true);
    setSaveError("");
    const result = await dispatch(completeCreditsIntroForAccount(projectId));
    setIsSaving(false);

    if (result?.success !== true) {
      setSaveError(
        result?.error || "We couldn't save this yet. Please try again.",
      );
      return;
    }

    localStorage.setItem(storageKey, "true");
    updateStoredUser(projectId);
    setCompletedProjectId(projectId);
    setIsOpen(false);
  };

  const openCreditSheet = () => {
    if (!creditSheetUrl) return;

    window.open(creditSheetUrl, "_blank", "noopener,noreferrer");
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="credits-intro-overlay" data-testid="credits-intro-overlay">
      <section
        className="credits-intro-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="credits-intro-title"
        aria-describedby="credits-intro-description"
      >
        <div className="credits-intro-accent" aria-hidden="true" />
        <h2 id="credits-intro-title">Manage your credits faster</h2>
        <p id="credits-intro-description">
          Use <strong>Credit Sheet</strong> to enter and edit your project
          credits, then use <strong>Sync Credits</strong> to pull the latest
          changes into ScrollX.
        </p>
        {saveError && (
          <p className="credits-intro-error" role="alert">
            {saveError}
          </p>
        )}
        <div className="credits-intro-actions">
          <button
            ref={primaryActionRef}
            className="credits-intro-primary"
            type="button"
            onClick={openCreditSheet}
            disabled={!creditSheetUrl}
          >
            Go to my Credit Sheet
          </button>
          <button
            className="credits-intro-secondary"
            type="button"
            onClick={completeIntro}
            disabled={isSaving}
          >
            {isSaving ? "Saving…" : "Got it"}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

export { storageKeyFor };
